import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Navigation } from './components/organisms/Navigation';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { MyAccountPage } from './pages/MyAccountPage';
import AgentPage from './pages/AgentPage';
import ResumesPage from './pages/ResumesPage';
import JobsPage from './pages/JobsPage';
import { ProtectedRoute } from './components/molecules/ProtectedRoute';
import { RoleProtectedRoute } from './components/molecules/RoleProtectedRoute';
import PanelPage from './pages/PanelPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import ExcelComparisonPage from './pages/ExcelComparisonPage';
import { createAppTheme } from './config/theme';

const theme = createAppTheme();

/**
 * Main App component
 * Root component with routing, theming, and global providers
 */
function AppContent() {
  const { user } = useAuth();

  return (
    <Router>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Navigation />
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/hr-dashboard"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr', 'sme']}>
                  <JobsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/sme-dashboard"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr', 'sme']}>
                  <ResumesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/interview-dashboard"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr', 'panelist']}>
                  <PanelPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <RoleProtectedRoute requiredRole="admin">
                  <AdminDashboardPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr', 'sme', 'panelist', 'user']}>
                  <MyAccountPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/agent"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr', 'sme', 'panelist', 'user']}>
                  <AgentPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/agent/:candidateId"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr', 'sme', 'panelist', 'user']}>
                  <AgentPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/excel-comparison"
              element={
                <RoleProtectedRoute requiredRoles={['admin', 'hr']}>
                  <ExcelComparisonPage />
                </RoleProtectedRoute>
              }
            />
          </Routes>
        </Box>
      </Box>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
